import { createContext, useCallback, useContext, useState, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subDays, subMonths, subYears, subWeeks } from 'date-fns';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Comparison presets:
 * - previous_period: same length, immediately before the primary range
 * - last_month: same day range, one month earlier
 * - last_year: same day range, one year earlier
 * - last_week: same day range, one week earlier
 * - custom: user-defined range
 * - none: comparison disabled
 */
export type ComparisonPreset =
  | 'previous_period'
  | 'last_month'
  | 'last_year'
  | 'last_week'
  | 'custom'
  | 'none';

interface DateRangeContextValue {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  timeZone: string;
  zonedNow: Date;
  globalRefresh: () => void;
  comparisonPreset: ComparisonPreset;
  setComparisonPreset: (preset: ComparisonPreset) => void;
  customComparisonRange: DateRange | null;
  setCustomComparisonRange: (range: DateRange | null) => void;
  /** The resolved comparison range based on current preset */
  comparisonRange: DateRange | null;
}

const DateRangeContext = createContext<DateRangeContextValue | null>(null);

export function useDateRange() {
  const ctx = useContext(DateRangeContext);
  if (!ctx) throw new Error('useDateRange must be used within DateRangeProvider');
  return ctx;
}

interface DateRangeProviderProps {
  children: ReactNode;
  timeZone?: string;
}

function resolveComparison(
  preset: ComparisonPreset,
  primary: DateRange,
  custom: DateRange | null,
): DateRange | null {
  if (preset === 'none') return null;
  if (preset === 'custom') return custom;

  const rangeDays = Math.round(
    (primary.endDate.getTime() - primary.startDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  switch (preset) {
    case 'previous_period': {
      const end = subDays(primary.startDate, 1);
      const start = subDays(end, rangeDays);
      return { startDate: start, endDate: end };
    }
    case 'last_month':
      return {
        startDate: subMonths(primary.startDate, 1),
        endDate: subMonths(primary.endDate, 1),
      };
    case 'last_year':
      return {
        startDate: subYears(primary.startDate, 1),
        endDate: subYears(primary.endDate, 1),
      };
    case 'last_week':
      return {
        startDate: subWeeks(primary.startDate, 1),
        endDate: subWeeks(primary.endDate, 1),
      };
    default:
      return null;
  }
}

export function DateRangeProvider({ children, timeZone = 'UTC' }: DateRangeProviderProps) {
  const now = new Date();
  const queryClient = useQueryClient();

  const [dateRange, setDateRangeState] = useState<DateRange>({
    startDate: subDays(now, 7),
    endDate: subDays(now, 1),
  });

  const [comparisonPreset, setComparisonPresetState] =
    useState<ComparisonPreset>('previous_period');
  const [customComparisonRange, setCustomComparisonRangeState] = useState<DateRange | null>(null);

  const setDateRange = useCallback((range: DateRange) => {
    setDateRangeState(range);
  }, []);

  const setComparisonPreset = useCallback((preset: ComparisonPreset) => {
    setComparisonPresetState(preset);
  }, []);

  const setCustomComparisonRange = useCallback((range: DateRange | null) => {
    setCustomComparisonRangeState(range);
  }, []);

  const globalRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  }, [queryClient]);

  const comparisonRange = useMemo(
    () => resolveComparison(comparisonPreset, dateRange, customComparisonRange),
    [comparisonPreset, dateRange, customComparisonRange],
  );

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        setDateRange,
        timeZone,
        zonedNow: now,
        globalRefresh,
        comparisonPreset,
        setComparisonPreset,
        customComparisonRange,
        setCustomComparisonRange,
        comparisonRange,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
}
